import { useSearchPlaidInstitutions } from '@/api/generated/plaid/plaid';
import { useEffect, useState } from 'react';

const DEBOUNCE_MS = 250;
/** Below this the query is too broad to be useful in the palette. */
const MIN_QUERY_LENGTH = 2;

/**
 * Debounced institution search for the add-account palette.
 *
 * <p>Backed by the server's cached institution catalog, so results keep coming even when the
 * upstream provider is down — the backend refreshes the cache in the background. Disabled for
 * categories no provider covers.
 */
export function usePlaidInstitutions(query: string, products: string[]) {
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const trimmed = debounced.trim();
  const canSearch = products.length > 0;
  const enabled = canSearch && trimmed.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isError } = useSearchPlaidInstitutions(
    { query: trimmed, product: products[0], limit: 20 },
    { query: { enabled, staleTime: 5 * 60 * 1000, retry: false } }
  );

  return {
    institutions: data?.items ?? [],
    isSearching: enabled && isFetching,
    /** The endpoint is unreachable — hide the automated section rather than dead-end the user. */
    isUnavailable: isError,
    canSearch,
  };
}
